import {
  Card,
  Col,
  Divider,
  EditButton,
  Row,
  Select,
  Show,
  Space,
  Tag,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useNavigation, useShow } from '@pankod/refine-core';
import { IRole } from 'interfaces';
import { useEffect, useState } from 'react';
import { getRolePermissions, getRoles } from 'services/role';
import { IPermissionObject, populateRequiredPermissions } from 'utilities/permission';
import { capitalize } from 'utilities/shared';

const { Text, Title } = Typography;

export const RoleShow = () => {
  const [isPermissionsLoading, setIsPermissionsLoading] = useState<boolean>(false);
  const [rolePermissions, setRolePermissions] = useState<Array<IPermissionObject>>([]);
  const [option, setOption] = useState<{ label: string; value: string } | null>(null);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { show } = useNavigation();
  const { queryResult } = useShow<IRole>({
    metaData: {
      fields: ['id', 'name', 'description'],
    },
  });
  const { data } = queryResult;
  const record = data?.data;

  useEffect(() => {
    if (!record) return;
    setOption({ label: record.name, value: record.id });

    async function fetchRolePermissions(roleId: string) {
      setIsPermissionsLoading(true);
      try {
        const response = await getRolePermissions(roleId);
        const populatedPermissions = await populateRequiredPermissions(response);
        setRolePermissions(populatedPermissions);
      } catch (err: any) {
        console.error(err.message);
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsPermissionsLoading(false);
    }
    fetchRolePermissions(record.id);

    return () => {
      setRolePermissions([]);
    };
  }, [record]);

  useEffect(() => {
    async function fetchRoles() {
      setIsLoading(true);
      try {
        const response = await getRoles();
        setRoles(response);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsLoading(false);
    }

    fetchRoles();
  }, []);

  const roleOptions = roles.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const onSelect = (option: any) => {
    if (roleOptions) {
      const selectedOption = roleOptions.find((item) => item.value === option);
      if (selectedOption) setOption(selectedOption);
    }
    show('roles', option);
  };

  const header = (
    <Space>
      <Space>
        <Text strong>Role</Text>
        <Select
          loading={isLoading}
          disabled={isLoading}
          value={option}
          options={roleOptions}
          style={{ width: '200px' }}
          filterOption={false}
          onSelect={onSelect}
        />
      </Space>

      <EditButton />
    </Space>
  );

  return (
    <Show isLoading={queryResult.isFetching} headerButtons={header}>
      <Row justify="space-around" align="middle">
        <Col xs={24} lg={20}>
          <Card title="Information">
            <Title level={5}>
              ID&nbsp;&nbsp;&nbsp;<Text style={{ color: '#2196F3' }}>#{record?.id}</Text>
            </Title>
            <Title level={5}>Name</Title>
            <Text>{record?.name}</Text>
            <Title level={5}>Description</Title>
            <Text>{record?.description}</Text>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Card title="Permissions" loading={isPermissionsLoading}>
        {rolePermissions.map((permission, idx) => {
          const { name, actions } = permission;

          return (
            <Card
              key={name}
              type="inner"
              title={name.toUpperCase()}
              extra={null}
              {...(idx !== 0 && { style: { marginTop: 16 } })}
            >
              {Object.keys(actions).map((action) => {
                const isPermitted = actions[action];

                if (isPermitted) {
                  return (
                    <Tag key={`${name}:${action}`} color="green">
                      {capitalize(action)}
                    </Tag>
                  );
                } else {
                  return (
                    <Tag key={`${name}:${action}`} color="red">
                      {capitalize(action)}
                    </Tag>
                  );
                }
              })}
            </Card>
          );
        })}
      </Card>
    </Show>
  );
};
